import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  UilTimes,
  UilMessage,
  UilHistory,
  UilPlus,
  UilCommentAlt,
} from '@iconscout/react-unicons';
import { CopilotBoltIcon } from './CopilotBoltIcon';
import { AuthContext } from 'components/lib';
import { WhitelabelContext } from 'app/whitelabel';
import CustomButton from 'components/custom/customButton';
import { useCopilot } from './useCopilot';
import { CopilotMessage } from './CopilotMessage';

const COLORS = {
  primary: '#0080FF',
  primaryHover: '#0066FF',
  primaryLight: '#E8F0F8',
  text: '#475569',
  textDark: '#1f2937',
  textLight: '#64748b',
  border: '#E5E5E5',
  bg: '#F9F9F9',
  bubbleAssistant: '#F2F2F2',
  white: '#FFFFFF',
  dangerBg: '#FEE2E2',
  dangerText: '#B91C1C',
};

function formatDate(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CopilotChat({ onClose }) {
  const [input, setInput] = useState('');
  const {
    messages,
    loading,
    streaming,
    error,
    conversations,
    view,
    setView,
    sendMessage,
    fetchConversations,
    loadConversation,
    startNewChat,
  } = useCopilot();
  const messagesEndRef = useRef(null);
  const { activePlan, getActivePlanDetails } = useContext(AuthContext);
  const { whitelabelDetails } = useContext(WhitelabelContext);
  const agencyName = whitelabelDetails?.agencyName || 'SalesBlink';

  const aiCredits = activePlan?.aiCredits;
  const hasPlanCredits = aiCredits && aiCredits.limit > 0;
  const isPlanWithoutCredits = aiCredits && aiCredits.limit === 0;
  const isCreditsExhausted = hasPlanCredits && aiCredits.used >= aiCredits.limit;
  const isCopilotBlocked = Boolean(isPlanWithoutCredits || isCreditsExhausted);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, view]);

  // Refresh plan/credit status when the panel opens so the empty-state blockers
  // reflect the latest usage (AuthContext only fetches this once on app load).
  useEffect(() => {
    if (getActivePlanDetails) {
      getActivePlanDetails();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading || streaming) return;

    const text = input.trim();
    setInput('');
    await sendMessage(text);
  };

  const handleShowHistory = () => {
    fetchConversations();
    setView(view === 'history' ? 'chat' : 'history');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: COLORS.white,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CopilotBoltIcon size={20} />
          <span style={{ fontSize: '16px', fontWeight: 600, color: COLORS.textDark }}>
            {agencyName} AI
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={startNewChat}
            title="New chat"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.white,
              color: COLORS.text,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = COLORS.bg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = COLORS.white;
            }}
          >
            <UilPlus size={14} />
            <span>New</span>
          </button>
          <button
            onClick={handleShowHistory}
            title="History"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
              backgroundColor: view === 'history' ? COLORS.primaryLight : COLORS.white,
              color: view === 'history' ? COLORS.primary : COLORS.text,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              if (view !== 'history') e.currentTarget.style.backgroundColor = COLORS.bg;
            }}
            onMouseLeave={(e) => {
              if (view !== 'history') e.currentTarget.style.backgroundColor = COLORS.white;
            }}
          >
            <UilHistory size={14} />
            <span>History</span>
          </button>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = COLORS.bg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <UilTimes size={20} color={COLORS.textLight} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          backgroundColor: COLORS.bg,
        }}
      >
        {view === 'history' ? (
          <div>
            <h3
              style={{
                margin: '0 0 12px',
                fontSize: '14px',
                fontWeight: 600,
                color: COLORS.textDark,
              }}
            >
              Recent chats
            </h3>
            {conversations.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  color: COLORS.textLight,
                  fontSize: '14px',
                  padding: '24px 0',
                }}
              >
                No conversations yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      color: COLORS.textDark,
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primaryLight;
                      e.currentTarget.style.borderColor = COLORS.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.white;
                      e.currentTarget.style.borderColor = COLORS.border;
                    }}
                  >
                    <UilCommentAlt size={16} color={COLORS.primary} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {conv.title || 'New conversation'}
                      </div>
                      <div style={{ fontSize: '12px', color: COLORS.textLight, marginTop: '2px' }}>
                        {formatDate(conv.date_updated)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  color: COLORS.textLight,
                  fontSize: '14px',
                  padding: '0 24px',
                }}
              >
                {isCopilotBlocked ? (
                  <>
                    <CopilotBoltIcon size={48} />
                    <p
                      style={{
                        marginTop: '16px',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: COLORS.textDark,
                      }}
                    >
                      {isPlanWithoutCredits ? 'Upgrade to Access' : 'Buy AI Credits'}
                    </p>
                    <p style={{ marginTop: '8px' }}>
                      {isPlanWithoutCredits
                        ? 'Your current plan does not allow you to do this.'
                        : 'You have used all your AI credits. Buy more credits from the billing page.'}
                    </p>
                    <CustomButton
                      title={isPlanWithoutCredits ? "Upgrade" : "Buy Credits"}
                      variant="primary"
                      onClick={() => { window.location.href = '/account/billing'; }}
                      style={{ marginTop: '16px' }}
                    />
                  </>
                ) : (
                  <>
                    <CopilotBoltIcon size={48} />
                    <p style={{ marginTop: '16px' }}>
                      Ask me to create sequences, manage contacts, check reports, or anything else
                      in {agencyName}.
                    </p>
                  </>
                )}
              </div>
            )}

            {messages
              .filter((message) => message.role !== 'system' && message.role !== 'tool')
              .map((message, index) => (
                <CopilotMessage key={index} message={message} />
              ))}

            {loading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div
                style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '12px',
                    backgroundColor: COLORS.bubbleAssistant,
                    color: COLORS.textLight,
                    fontSize: '14px',
                  }}
                >
                  Thinking...
                </div>
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  marginBottom: '12px',
                  borderRadius: '8px',
                  backgroundColor: COLORS.dangerBg,
                  color: COLORS.dangerText,
                  fontSize: '13px',
                }}
              >
                {error}
              </div>
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {view === 'chat' && (
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            borderTop: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Ask ${agencyName} AI...`}
            disabled={loading || (isCopilotBlocked && messages.length === 0)}
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: '8px',
              border: `1px solid ${COLORS.border}`,
              fontSize: '14px',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = COLORS.primary;
              e.currentTarget.style.boxShadow = `0 0 0 3px rgba(0, 128, 255, 0.15)`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = COLORS.border;
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <button
            type="submit"
            disabled={loading || streaming || !input.trim() || (isCopilotBlocked && messages.length === 0)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor:
                loading || streaming || !input.trim() || (isCopilotBlocked && messages.length === 0)
                  ? COLORS.border
                  : COLORS.primary,
              color: COLORS.white,
              cursor:
                loading || streaming || !input.trim() || (isCopilotBlocked && messages.length === 0)
                  ? 'not-allowed'
                  : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (
                !loading &&
                !streaming &&
                input.trim() &&
                !(isCopilotBlocked && messages.length === 0)
              )
                e.currentTarget.style.backgroundColor = COLORS.primaryHover;
            }}
            onMouseLeave={(e) => {
              if (
                !loading &&
                !streaming &&
                input.trim() &&
                !(isCopilotBlocked && messages.length === 0)
              )
                e.currentTarget.style.backgroundColor = COLORS.primary;
            }}
          >
            <UilMessage size={18} />
          </button>
        </form>
      )}
    </div>
  );
}
